import React, { useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import Layout from 'components/Layout';
import BeOnline from './BeOnline';
import OnlinePayment from './OnlinePayment';
import useGetMerchantDocumentByScreen from 'hooks/useGetMerchantDocumentByScreen';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { getMerchantV2 } from 'services/merchantService';
import { useIsFetching, useQuery } from 'react-query';
import { Merchant } from 'types/Merchant';
import Loader from 'components/Loader';
import { Plans } from './Plans';

const secondNavData: Parameters<typeof Layout>['0']['secondNavs'] = [
  { label: 'Formules', path: '/plans' },
  { label: 'Activer votre site', path: '/be-online' },
  { label: 'Activer le paiement en ligne', path: '/online-payment' }
];

const MerchantRegistration = () => {
  const { path } = useRouteMatch<{ path: string }>();
  const { data, isLoading: isGettingMerchantDocuments } =
    useGetMerchantDocumentByScreen('be_online');
  const { merchantSlug } = useSelector((state: RootState) => state.user);
  const { id } = useSelector((state: RootState) => state.subscriptionPlan);
  const isFetching = useIsFetching(['getMerchant']) > 0;

  const { data: dataMerchant, isLoading: isGettingMerchantInfo } =
    useQuery<Merchant>(
      'getMerchant',
      ({ signal }) => getMerchantV2(merchantSlug, signal),
      { staleTime: Infinity, enabled: true }
    );

  const hasSubscriptionPlanSelected = dataMerchant?.subscription
    ? Boolean(dataMerchant.subscription.id)
    : Boolean(id);
  const isReady =
    !isGettingMerchantInfo && !isGettingMerchantDocuments && !isFetching;

  // only enable online payment tab if be online tab's documents
  // have been submitted.
  const shouldDisableOnlinePayment = useMemo(() => {
    // temporarily set to false
    // this will prevent redirecting to be-online path
    // when user is on online-payment path and the query is loading
    if (!data || !dataMerchant || dataMerchant.enabled) {
      return false;
    }

    return data['hydra:member'].every((item) => item.state === 'new');
  }, [data, dataMerchant]);

  const secondNav = secondNavData.map((route) => {
    const isBeOnline = route.path === '/be-online';
    const isOnlinePay = route.path === '/online-payment';

    const disableBecauseNoSub =
      !hasSubscriptionPlanSelected && (isBeOnline || isOnlinePay);
    const disableBecauseDocs = isOnlinePay && shouldDisableOnlinePayment;

    return disableBecauseNoSub || disableBecauseDocs
      ? { ...route, isDisabled: true }
      : route;
  });

  return (
    <>
      {(isGettingMerchantInfo || isGettingMerchantDocuments) && <Loader />}
      <Layout secondNavs={secondNav}>
        <Switch>
          <Route path={`${path}/plans`}>
            <Plans />
          </Route>
          <GuardRoute
            path={`${path}/be-online`}
            when={isReady && hasSubscriptionPlanSelected}
            redirectTo={`${path}/plans`}>
            <BeOnline />
          </GuardRoute>
          <GuardRoute
            path={`${path}/online-payment`}
            when={
              isReady &&
              hasSubscriptionPlanSelected &&
              !shouldDisableOnlinePayment
            }
            redirectTo={
              hasSubscriptionPlanSelected
                ? `${path}/be-online`
                : `${path}/plans`
            }>
            <OnlinePayment />
          </GuardRoute>
          <Redirect from="*" to={`${path}/plans`} />
        </Switch>
      </Layout>
    </>
  );
};

export default MerchantRegistration;

const GuardRoute: React.FC<{
  path: string;
  when: boolean;
  redirectTo: string;
}> = ({ when, redirectTo, children, ...rest }) => {
  return (
    <Route
      {...rest}
      render={({ location }) =>
        when || (location.state as any)?.allowGuardBypass ? (
          <>{children}</>
        ) : (
          <Redirect to={{ pathname: redirectTo, state: { from: location } }} />
        )
      }
    />
  );
};
